import {
  HsaConditionExpression,
  HsaConditionLeaf,
  HsaConditionLeafKind,
  HsaDateConditionOperator,
  HsaMultiSelectConditionOperator,
  HsaNumericConditionOperator,
  HsaSingleSelectConditionOperator,
  HsaTextConditionOperator,
} from "@redotech/redo-conversion-hsa-common/hsa-condition";
import { HsaQuestion } from "@redotech/redo-conversion-hsa-common/hsa-question-type";
import { ConjunctionMode } from "@redotech/redo-customer-segments-common/segments/segment-types";
import { StepType } from "@redotech/redo-flows-core-schema-common/common";
import { t } from "@redotech/redo-i18n";
import { DeepPartial } from "@redotech/util/type";
import { PartialFlowStep } from "../steps";
import { ConditionSummarySegment } from "./condition-summary";

const MAX_LIST_DISPLAY = 3;

/** Pool questions hydrated onto the flow's group steps, keyed by handle. */
export type HsaQuestionLookup = ReadonlyMap<string, DeepPartial<HsaQuestion>>;

export function buildHsaQuestionLookup(
  steps: readonly PartialFlowStep[],
): HsaQuestionLookup {
  const lookup = new Map<string, DeepPartial<HsaQuestion>>();
  for (const step of steps) {
    if (step.type !== StepType.HSA_QUESTION_GROUP) {
      continue;
    }
    for (const entry of step.questions ?? []) {
      if (entry?.handle && entry.question) {
        lookup.set(entry.handle, entry.question);
      }
    }
  }
  return lookup;
}

function hsaConjunctionLabel(mode: ConjunctionMode): string {
  return mode === ConjunctionMode.OR ? t("or") : t("and");
}

export function numericOperatorLabels(): Record<
  HsaNumericConditionOperator,
  string
> {
  return {
    [HsaNumericConditionOperator.EQUALS]: t("is"),
    [HsaNumericConditionOperator.NOT_EQUALS]: t("is not"),
    [HsaNumericConditionOperator.LESS_THAN]: t("is less than"),
    [HsaNumericConditionOperator.LESS_THAN_OR_EQUAL]: t("is at most"),
    [HsaNumericConditionOperator.GREATER_THAN]: t("is greater than"),
    [HsaNumericConditionOperator.GREATER_THAN_OR_EQUAL]: t("is at least"),
  };
}

export function textOperatorLabels(): Record<HsaTextConditionOperator, string> {
  return {
    [HsaTextConditionOperator.EQUALS]: t("is"),
    [HsaTextConditionOperator.NOT_EQUALS]: t("is not"),
    [HsaTextConditionOperator.CONTAINS]: t("contains"),
    [HsaTextConditionOperator.NOT_CONTAINS]: t("does not contain"),
    [HsaTextConditionOperator.MATCHES_REGEX]: t("matches pattern"),
    [HsaTextConditionOperator.IS_EMPTY]: t("is empty"),
    [HsaTextConditionOperator.IS_NOT_EMPTY]: t("is not empty"),
  };
}

export function dateOperatorLabels(): Record<HsaDateConditionOperator, string> {
  return {
    [HsaDateConditionOperator.AGE_LESS_THAN]: t("age is under"),
    [HsaDateConditionOperator.AGE_AT_LEAST]: t("age is at least"),
  };
}

export function multiSelectOperatorLabels(): Record<
  HsaMultiSelectConditionOperator,
  string
> {
  return {
    [HsaMultiSelectConditionOperator.INCLUDES_ANY]: t("includes any of"),
    [HsaMultiSelectConditionOperator.INCLUDES_ALL]: t("includes all of"),
    [HsaMultiSelectConditionOperator.INCLUDES_NONE]: t("includes none of"),
    [HsaMultiSelectConditionOperator.IS_EXACTLY]: t("is exactly"),
    [HsaMultiSelectConditionOperator.IS_EMPTY]: t("has no selection"),
    [HsaMultiSelectConditionOperator.IS_NOT_EMPTY]: t("has a selection"),
  };
}

const text = (value: string): ConditionSummarySegment => ({
  kind: "text",
  text: value,
});
const value = (raw: string | number): ConditionSummarySegment => ({
  kind: "value",
  text: String(raw),
});

function optionLabel(
  question: DeepPartial<HsaQuestion> | undefined,
  optionValue: string,
): string {
  if (question && "options" in question) {
    return (
      question.options?.find((option) => option?.value === optionValue)
        ?.label ?? optionValue
    );
  }
  return optionValue;
}

function valueListSegments(
  values: readonly string[],
): ConditionSummarySegment[] {
  const shown = values.slice(0, MAX_LIST_DISPLAY).map(value);
  return values.length > MAX_LIST_DISPLAY
    ? [
        ...shown,
        text(t("+{{count}} more", { count: values.length - MAX_LIST_DISPLAY })),
      ]
    : shown;
}

export function getHsaConditionLeafSummary(
  leaf: DeepPartial<HsaConditionLeaf> | undefined,
  lookup: HsaQuestionLookup,
): ConditionSummarySegment[] {
  if (!leaf?.questionHandle) {
    return [];
  }
  const question = lookup.get(leaf.questionHandle);
  const variable: ConditionSummarySegment = {
    kind: "variable",
    text: question?.label ?? leaf.questionHandle,
  };
  // tslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check -- TODO: grandfather FIXME

  switch (leaf.leafKind) {
    case HsaConditionLeafKind.NUMERIC: {
      const operator = leaf.operator
        ? numericOperatorLabels()[leaf.operator]
        : undefined;
      return operator !== undefined && leaf.value !== undefined
        ? [variable, text(operator), value(leaf.value)]
        : [variable];
    }
    case HsaConditionLeafKind.TEXT: {
      const operator = leaf.operator
        ? textOperatorLabels()[leaf.operator]
        : undefined;
      if (operator === undefined) {
        return [variable];
      }
      const takesValue =
        leaf.operator !== HsaTextConditionOperator.IS_EMPTY &&
        leaf.operator !== HsaTextConditionOperator.IS_NOT_EMPTY;
      return takesValue && leaf.value !== undefined
        ? [variable, text(operator), value(leaf.value)]
        : [variable, text(operator)];
    }
    case HsaConditionLeafKind.DATE: {
      const operator = leaf.operator
        ? dateOperatorLabels()[leaf.operator]
        : undefined;
      return operator !== undefined && leaf.value !== undefined
        ? [variable, text(operator), value(leaf.value)]
        : [variable];
    }
    case HsaConditionLeafKind.SINGLE_SELECT: {
      const labels = (leaf.optionValues ?? []).flatMap((optionValue) =>
        optionValue === undefined ? [] : [optionLabel(question, optionValue)],
      );
      const single = labels.length === 1;
      const operator =
        leaf.operator === HsaSingleSelectConditionOperator.IS_NONE_OF
          ? single
            ? t("is not")
            : t("is none of")
          : leaf.operator === HsaSingleSelectConditionOperator.IS_ANY_OF
            ? single
              ? t("is")
              : t("is any of")
            : undefined;
      return operator !== undefined
        ? [variable, text(operator), ...valueListSegments(labels)]
        : [variable];
    }
    case HsaConditionLeafKind.MULTI_SELECT: {
      const operator = leaf.operator
        ? multiSelectOperatorLabels()[leaf.operator]
        : undefined;
      if (operator === undefined) {
        return [variable];
      }
      const takesValues =
        leaf.operator !== HsaMultiSelectConditionOperator.IS_EMPTY &&
        leaf.operator !== HsaMultiSelectConditionOperator.IS_NOT_EMPTY;
      if (!takesValues) {
        return [variable, text(operator)];
      }
      const labels = (leaf.optionValues ?? []).flatMap((optionValue) =>
        optionValue === undefined ? [] : [optionLabel(question, optionValue)],
      );
      return [variable, text(operator), ...valueListSegments(labels)];
    }
    default:
      return [variable];
  }
}

export function getHsaConditionLeafQuestionLabel(
  leaf: DeepPartial<HsaConditionLeaf> | undefined,
  lookup: HsaQuestionLookup,
): string | undefined {
  if (!leaf?.questionHandle) {
    return undefined;
  }
  return lookup.get(leaf.questionHandle)?.label ?? leaf.questionHandle;
}

function joinOr(values: readonly string[]): string {
  return values.length <= 1
    ? values.join("")
    : `${values.slice(0, -1).join(", ")} or ${values[values.length - 1]}`;
}

/**
 * The answer that satisfies a leaf, phrased for "Matching answer: …" — e.g.
 * "Female", "Not Female", "Age is under 18", "Includes any of Anxiety, Back pain".
 */
export function getHsaConditionLeafAnswerText(
  leaf: DeepPartial<HsaConditionLeaf> | undefined,
  lookup: HsaQuestionLookup,
): string | undefined {
  const [, ...rest] = getHsaConditionLeafSummary(leaf, lookup);
  if (rest.length === 0) {
    return undefined;
  }
  const values = rest.filter((s) => s.kind === "value").map((s) => s.text);
  const words = rest.filter((s) => s.kind === "text").map((s) => s.text);
  const overflow = words.filter((w) => w.startsWith("+"));
  if (leaf?.leafKind === HsaConditionLeafKind.SINGLE_SELECT) {
    const list = [joinOr(values), ...overflow].filter(Boolean).join(" ");
    return leaf.operator === HsaSingleSelectConditionOperator.IS_NONE_OF
      ? `Not ${list}`
      : list;
  }
  const operator = words.filter((w) => !w.startsWith("+")).join(" ");
  const phrase = [operator, values.join(", "), ...overflow]
    .filter(Boolean)
    .join(" ");
  return phrase.charAt(0).toUpperCase() + phrase.slice(1);
}

export interface HsaConditionLeafEntry {
  leaf: DeepPartial<HsaConditionLeaf>;
  /** Conjunction shown before this leaf: inner within a block, outer between. */
  connector?: string;
}

/** Flattens the two-level expression into renderable leaves with connectors. */
export function flattenHsaConditionLeaves(
  expression: DeepPartial<HsaConditionExpression> | undefined,
): HsaConditionLeafEntry[] {
  const outer = hsaConjunctionLabel(
    expression?.conjunction ?? ConjunctionMode.AND,
  );
  const entries: HsaConditionLeafEntry[] = [];
  for (const block of expression?.conditionBlocks ?? []) {
    const inner = hsaConjunctionLabel(
      block?.conjunction ?? ConjunctionMode.AND,
    );
    const leaves = (block?.conditions ?? []).filter(
      (leaf): leaf is DeepPartial<HsaConditionLeaf> => !!leaf?.questionHandle,
    );
    for (const [index, leaf] of leaves.entries()) {
      entries.push({
        leaf,
        ...(entries.length > 0 && { connector: index === 0 ? outer : inner }),
      });
    }
  }
  return entries;
}
